import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

// Types for our toast messages
export type ToastType = "error" | "warning" | "success" | "info";
export interface ToastMessage {
	id: string;
	type: ToastType;
	message: string;
	expiresAt: number;
}

// Container for the toast messages
const ToastContainer = styled.div`
	position: fixed;
	bottom: 20px;
	left: 20px;
	display: flex;
	flex-direction: column-reverse;
	gap: 10px;
	z-index: 9999;
`;

// Individual toast message
const Toast = styled.div<{ type: ToastType }>`
	box-sizing: border-box;
	position: relative;
	width: 260px;
	min-height: 35px;
	padding: 8px 30px 8px 40px;
	background: ${({ type }) => {
		switch (type) {
			case "error":
				return "#FFEDED";
			case "warning":
				return "#FFF5EA";
			case "success":
				return "#EDFFF3";
			case "info":
			default:
				return "#EDF6FF";
		}
	}};
	border: 1px solid
		${({ type }) => {
			switch (type) {
				case "error":
					return "#EF4444";
				case "warning":
					return "#F79009";
				case "success":
					return "#16A34A";
				case "info":
				default:
					return "#2563EB";
			}
		}};
	border-radius: 3px;
	animation: slideIn 0.3s ease-in-out;
	transition: all 0.3s ease;

	@keyframes slideIn {
		from {
			transform: translateX(-100%);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}
`;

const IconCircle = styled.div<{ type: ToastType }>`
	box-sizing: border-box;
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid
		${({ type }) => {
			switch (type) {
				case "error":
					return "#EF4444";
				case "warning":
					return "#F79009";
				case "success":
					return "#16A34A";
				case "info":
				default:
					return "#2563EB";
			}
		}};
	border-radius: 50%;
	background: transparent;
`;

const IconText = styled.span<{ type: ToastType }>`
	font-family: "Poppins", sans-serif;
	font-style: normal;
	font-weight: 400;
	font-size: 11px;
	color: ${({ type }) => {
		switch (type) {
			case "error":
				return "#EF4444";
			case "warning":
				return "#F79009";
			case "success":
				return "#16A34A";
			case "info":
			default:
				return "#2563EB";
		}
	}};
`;

const MessageText = styled.span<{ type: ToastType }>`
	font-family: "Poppins", sans-serif;
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 16px;
	display: flex;
	align-items: center;
	color: ${({ type }) => {
		switch (type) {
			case "error":
				return "#EF4444";
			case "warning":
				return "#F79009";
			case "success":
				return "#16A34A";
			case "info":
			default:
				return "#2563EB";
		}
	}};
`;

const CloseButton = styled.button<{ type: ToastType }>`
	position: absolute;
	top: 8px;
	right: 8px;
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: transparent;
	cursor: pointer;
	padding: 0;

	&::before,
	&::after {
		content: "";
		position: absolute;
		width: 10px;
		height: 1px;
		background-color: ${({ type }) => {
			switch (type) {
				case "error":
					return "#EF4444";
				case "warning":
					return "#F79009";
				case "success":
					return "#16A34A";
				case "info":
				default:
					return "#2563EB";
			}
		}};
	}

	&::before {
		transform: rotate(45deg);
	}

	&::after {
		transform: rotate(-45deg);
	}
`;

// Icon component based on toast type
const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
	const getIconContent = () => {
		switch (type) {
			case "error":
				return "!";
			case "warning":
				return "!";
			case "success":
				return "✓";
			case "info":
			default:
				return "i";
		}
	};

	return (
		<IconCircle type={type}>
			<IconText type={type}>{getIconContent()}</IconText>
		</IconCircle>
	);
};

const ToastItem: React.FC<{
	toast: ToastMessage;
	onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
	const [isHovering, setIsHovering] = useState(false);
	const pausedTimeRef = useRef<number | null>(null);
	const remainingTimeRef = useRef<number | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const setExpirationTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		const now = Date.now();
		const timeLeft = remainingTimeRef.current !== null ? remainingTimeRef.current : toast.expiresAt - now;

		if (timeLeft > 0) {
			timeoutRef.current = setTimeout(() => {
				onClose(toast.id);
			}, timeLeft);
		} else {
			onClose(toast.id);
		}
	}, [onClose, toast.id, toast.expiresAt]);

	// Set up initial timeout
	useEffect(() => {
		setExpirationTimeout();

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [setExpirationTimeout]);

	// Handle hover state
	useEffect(() => {
		if (isHovering) {
			// Pause the timer
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			// Save when we paused
			pausedTimeRef.current = Date.now();
			// Calculate remaining time
			remainingTimeRef.current = toast.expiresAt - pausedTimeRef.current;
		} else {
			// Resume the timer if we were paused
			if (pausedTimeRef.current !== null) {
				setExpirationTimeout();
				pausedTimeRef.current = null;
			}
		}
	}, [isHovering, setExpirationTimeout, toast.expiresAt]);

	return (
		<Toast type={toast.type} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
			<ToastIcon type={toast.type} />
			<MessageText type={toast.type}>{toast.message}</MessageText>
			<CloseButton type={toast.type} onClick={() => onClose(toast.id)} aria-label="Close" />
		</Toast>
	);
};

// Context to manage toast messages globally
export const ToastContext = React.createContext<{
	addToast: (type: ToastType, message: string) => void;
	removeToast: (id: string) => void;
	clearAllToasts: () => void;
}>({
	addToast: () => {},
	removeToast: () => {},
	clearAllToasts: () => {},
});

// Provider component for toast notifications
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastMessage[]>([]);
	const location = useLocation();

	const clearAllToasts = () => {
		setToasts([]);
	};

	const addToast = (type: ToastType, message: string) => {
		// Limit to 5 messages
		if (toasts.length >= 5) {
			setToasts((prev) => prev.slice(1));
		}

		const newToast: ToastMessage = {
			id: Date.now().toString(),
			type,
			message,
			expiresAt: Date.now() + 4000, // 4 seconds from now
		};

		setToasts((prev) => [...prev, newToast]);
	};

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	};

	useEffect(() => {
		clearAllToasts();
	}, [location.pathname]);

	return (
		<ToastContext.Provider value={{ addToast, removeToast, clearAllToasts }}>
			{children}
			<ToastContainer>
				{toasts.map((toast) => (
					<ToastItem key={toast.id} toast={toast} onClose={removeToast} />
				))}
			</ToastContainer>
		</ToastContext.Provider>
	);
};

// Custom hook to use toast notifications
export const useToast = () => {
	const context = React.useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};

// Direct error message component for backward compatibility
export const ToastError: React.FC<{ message: string }> = ({ message }) => {
	const { addToast } = useToast();

	useEffect(() => {
		if (message) {
			addToast("error", message);
		}
	}, [message, addToast]);

	return null;
};
